import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

const NEWSLETTER_URL = "https://readdy.ai/api/form/da2jcubd3r93mn1k3a10";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检测：若被自动填充则伪装成功，不提交
    const honeypot = (formData.get("website_alt") as string) || "";
    if (honeypot.trim() !== "") {
      setStatus("success");
      setMessage("订阅成功，感谢你的关注！");
      form.reset();
      setEmail("");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "website_alt") payload.append(key, value as string);
    });

    setStatus("loading");
    try {
      const res = await fetch(NEWSLETTER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.message || responseText;
      const isSpam =
        !res.ok || !parsed?.code || /spam/i.test(responseText) || /spam/i.test(serverMsg || "");
      if (isSpam) {
        setStatus("error");
        setMessage(serverMsg && serverMsg !== "form data is spam" ? serverMsg : "提交失败，请稍后再试");
        return;
      }
      setStatus("success");
      setMessage(parsed?.meta?.message || "订阅成功，欢迎加入影页社！");
      form.reset();
      setEmail("");
    } catch {
      setStatus("error");
      setMessage("网络异常，请稍后重试");
    }
  }

  return (
    <footer className="bg-secondary-950 text-background-100">
      {/* 顶部订阅带 */}
      <div className="border-b border-secondary-800">
        <div className="mx-auto grid w-full max-w-7xl gap-6 px-4 md:px-6 py-10 md:py-12 md:grid-cols-2 items-center">
          <div>
            <p className="font-display text-2xl md:text-3xl text-background-50">订阅影页社 · 每日同人精选</p>
            <p className="mt-2 text-sm text-background-300">
              新连载、新壁纸、新短篇，第一时间送达你的邮箱。不打扰，只分享。
            </p>
          </div>
          <form
            data-readdy-form="da2jcubd3r93mn1k3a10"
            onSubmit={handleSubmit}
            className="flex flex-col gap-3 sm:flex-row sm:items-center"
          >
            <input
              type="email"
              name="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="请输入你的邮箱"
              className="flex-1 rounded-full border border-secondary-700 bg-secondary-900 px-5 py-3 text-sm text-background-50 placeholder:text-background-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/30"
            />
            <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="website_alt" />
            <button
              type="submit"
              disabled={status === "loading"}
              className="inline-flex items-center justify-center gap-1 rounded-full bg-primary-500 px-6 py-3 font-label text-sm whitespace-nowrap text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
            >
              {status === "loading" ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>}
              立即订阅
            </button>
          </form>
        </div>
        {status !== "idle" && (
          <div className="mx-auto w-full max-w-7xl px-4 md:px-6 pb-6">
            <p
              className={`rounded-md px-4 py-2 font-label text-sm ${
                status === "success" ? "bg-primary-900/50 text-primary-200" : "bg-accent-900/50 text-accent-200"
              }`}
              role={status === "error" ? "alert" : "status"}
            >
              {message}
            </p>
          </div>
        )}
      </div>

      {/* 主体页脚 */}
      <div className="mx-auto grid w-full max-w-7xl gap-8 px-4 md:px-6 py-12 md:py-14 sm:grid-cols-2 lg:grid-cols-4">
        <div className="sm:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-2">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-quill-pen-fill text-lg"></i>
            </span>
            <span className="font-display text-2xl text-background-50">影页社</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-background-300">
            专注火影忍者同人图文与小说的一站式内容站。每日精选同人小说、同人文与同人图，提供高清壁纸、插画下载与短篇速读推荐。
          </p>
          <div className="mt-5 flex items-center gap-2">
            {[
              { icon: "ri-twitter-x-line", label: "X" },
              { icon: "ri-pinterest-line", label: "Pinterest" },
              { icon: "ri-instagram-line", label: "Instagram" },
              { icon: "ri-weibo-line", label: "微博" },
            ].map((s) => (
              <a
                key={s.label}
                href="#"
                onClick={(e) => e.preventDefault()}
                aria-label={s.label}
                className="grid h-9 w-9 place-items-center rounded-full bg-secondary-900 text-background-300 transition-colors hover:bg-primary-500 hover:text-background-50"
              >
                <i className={s.icon}></i>
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="font-label text-sm font-semibold tracking-wider text-background-50">内容栏目</p>
          <ul className="mt-4 space-y-3 text-sm text-background-300">
            <li><Link to="/xiaoshuo" className="hover:text-primary-300 transition-colors">同人小说</Link></li>
            <li><Link to="/tongrentu" className="hover:text-primary-300 transition-colors">同人图 · 插画</Link></li>
            <li><Link to="/tongrentu" className="hover:text-primary-300 transition-colors">高清壁纸下载</Link></li>
            <li><Link to="/duanpian" className="hover:text-primary-300 transition-colors">短篇速读</Link></li>
          </ul>
        </div>

        <div>
          <p className="font-label text-sm font-semibold tracking-wider text-background-50">站点导航</p>
          <ul className="mt-4 space-y-3 text-sm text-background-300">
            <li><Link to="/" className="hover:text-primary-300 transition-colors">首页</Link></li>
            <li><Link to="/guanyu" className="hover:text-primary-300 transition-colors">关于我们</Link></li>
            <li><Link to="/guanyu" className="hover:text-primary-300 transition-colors">投稿指南</Link></li>
            <li><Link to="/guanyu#faq" className="hover:text-primary-300 transition-colors">常见问题</Link></li>
          </ul>
        </div>

        <div>
          <p className="font-label text-sm font-semibold tracking-wider text-background-50">联系我们</p>
          <ul className="mt-4 space-y-3 text-sm text-background-300">
            <li className="flex items-center gap-2"><i className="ri-mail-line text-primary-300"></i> yingye.club@example.com</li>
            <li className="flex items-center gap-2"><i className="ri-time-line text-primary-300"></i> 每日 9:00 — 23:00</li>
            <li className="flex items-center gap-2"><i className="ri-message-3-line text-primary-300"></i> 投稿 / 反馈均可来信</li>
          </ul>
        </div>
      </div>

      {/* 版权 */}
      <div className="border-t border-secondary-800">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-3 px-4 md:px-6 py-5 text-xs text-background-400 sm:flex-row">
          <p>© {new Date().getFullYear()} 影页社 · 火影忍者同人图文站。本站为粉丝同人站点，与官方无关。</p>
          <p className="flex items-center gap-2">
            <i className="ri-leaf-line text-primary-300"></i> 用热爱，守护每一份同人创作
          </p>
        </div>
      </div>
    </footer>
  );
}